import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ResponsesList() {
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [columnOrder, setColumnOrder] = useState([]);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [customColumns, setCustomColumns] = useState([]);
  const [showCustomColumnModal, setShowCustomColumnModal] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnFormula, setNewColumnFormula] = useState('');
  const [formulaType, setFormulaType] = useState('sum');
  const [columnSearch, setColumnSearch] = useState('');
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [allColumnsOrder, setAllColumnsOrder] = useState([]);

  useEffect(() => {
    loadSurveys();
  }, []);

  useEffect(() => {
    if (surveyData) {
      const questions = getQuestions();
      if (questions.length > 0 && allColumnsOrder.length === 0) {
        // Initialize all columns order with questions, custom columns, and metadata
        const initialOrder = [
          ...questions.map((_, idx) => ({ type: 'question', index: idx })),
          ...customColumns.map((col) => ({ type: 'custom', id: col.id })),
          { type: 'metadata', id: 'addedBy' },
          { type: 'metadata', id: 'addedAt' }
        ];
        setAllColumnsOrder(initialOrder);
      }
    }
  }, [surveyData, customColumns]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Only close if clicking outside the dropdown area and search input
      if (showColumnDropdown && 
          !e.target.closest('.column-search-container') && 
          !e.target.closest('.column-dropdown')) {
        setShowColumnDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnDropdown]);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/surveys`);
      setSurveys(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load forms: ' + err.message);
      setLoading(false);
    }
  };

  const loadSurveyResponses = async (surveyId) => {
    try {
      setLoading(true);
      setSelectedSurvey(surveyId);
      const response = await axios.get(`${API_URL}/responses/${surveyId}`);
      setResponses(response.data.responses);
      setSurveyData(response.data.survey);
      setLoading(false);
    } catch (err) {
      setError('Failed to load responses: ' + err.message);
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedSurvey(null);
    setResponses([]);
    setSurveyData(null);
    setAllColumnsOrder([]);
  };

  const getQuestions = () => {
    if (!surveyData?.json?.pages) return [];
    
    const questions = [];
    surveyData.json.pages.forEach(page => {
      if (page.elements) {
        page.elements.forEach(element => {
          questions.push({
            name: element.name,
            title: element.title || element.name
          });
        });
      }
    });
    return questions;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Drag and drop handlers
  const handleDragStart = (e, columnIndex) => {
    setDraggedColumn(columnIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetIndex) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === targetIndex) return;

    const newOrder = [...allColumnsOrder];
    const draggedItem = newOrder[draggedColumn];
    
    newOrder.splice(draggedColumn, 1);
    newOrder.splice(targetIndex, 0, draggedItem);
    
    setAllColumnsOrder(newOrder);
    setDraggedColumn(targetIndex);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const addCustomColumn = () => {
  if (!newColumnName.trim()) {
    alert('Please enter column name');
    return;
  }

  const newColumn = {
    id: Date.now(),
    name: newColumnName,
    type: formulaType,
    formula: newColumnFormula,
    sourceColumns: newColumnFormula.split(',').map(s => s.trim()).filter(Boolean)
  };

  setCustomColumns([...customColumns, newColumn]);
  
  // Add to unified column order (insert before metadata columns)
  const metadataStartIndex = allColumnsOrder.findIndex(col => col.type === 'metadata');
  const newOrder = [...allColumnsOrder];
  if (metadataStartIndex !== -1) {
    newOrder.splice(metadataStartIndex, 0, { type: 'custom', id: newColumn.id });
  } else {
    newOrder.push({ type: 'custom', id: newColumn.id });
  }
  setAllColumnsOrder(newOrder);
  
  setShowCustomColumnModal(false);
  setNewColumnName('');
  setNewColumnFormula('');
  setFormulaType('sum');
};

// Calculate custom column value
const calculateCustomValue = (customColumn, responseData) => {
  const questions = getQuestions();
  const values = customColumn.sourceColumns.map(colName => {
    const question = questions.find(q => q.name === colName || q.title === colName);
    if (question) {
      return responseData[question.name];
    }
    return null;
  }).filter(v => v !== null && v !== undefined);

  try {
    switch (customColumn.type) {
      case 'sum':
        return values.reduce((acc, val) => {
          const num = parseFloat(val);
          return acc + (isNaN(num) ? 0 : num);
        }, 0);

      case 'concat':
        return values.join(' ');

      case 'merge':
        return values.join(', ');

      case 'average':
        const nums = values.map(v => parseFloat(v)).filter(n => !isNaN(n));
        return nums.length > 0 ? (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2) : 0;

      case 'count':
        return values.filter(v => v && v !== '').length;

      case 'custom':
        let expression = customColumn.formula;
        customColumn.sourceColumns.forEach((colName) => {
          const question = questions.find(q => q.name === colName || q.title === colName);
          if (question) {
            const value = responseData[question.name];
            const numValue = parseFloat(value) || 0;
            expression = expression.replace(new RegExp(colName, 'g'), numValue);
          }
        });
        // eslint-disable-next-line no-eval
        return eval(expression);

      default:
        return '-';
    }
  } catch (error) {
    return 'Error';
  }
};

// Delete custom column
  const deleteCustomColumn = (columnId) => {
    setCustomColumns(customColumns.filter(col => col.id !== columnId));
    setAllColumnsOrder(allColumnsOrder.filter(col => !(col.type === 'custom' && col.id === columnId)));
  };

  const exportToExcel = () => {
    const questions = getQuestions();
    
    // Create CSV content
    const headers = ['#', ...questions.map(q => q.title), 'Added By', 'Added At'];
    const rows = responses.map((response, idx) => [
      idx + 1,
      ...questions.map(q => formatValue(response.responses?.[q.name])),
      response.userName,
      new Date(response.submittedAt).toLocaleString()
    ]);

    // Convert to CSV
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${surveyData?.title || 'survey-responses'}-${new Date().getTime()}.csv`;
    link.click();
  };

  const exportToPDF = () => {
    const questions = getQuestions();
    const printWindow = window.open('', '_blank');
    
    const tableRows = responses.map((response, idx) => `
      <tr>
        <td>${idx + 1}</td>
        ${questions.map(q => `<td>${formatValue(response.responses?.[q.name])}</td>`).join('')}
        <td>${response.userName}</td>
        <td>${new Date(response.submittedAt).toLocaleString()}</td>
      </tr>
    `).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${surveyData?.title || 'Survey Responses'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #2c3e50; margin-bottom: 10px; }
          .meta { color: #7f8c8d; margin-bottom: 20px; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 11px; }
          th { background-color: #2c3e50; color: white; font-weight: 600; }
          tr:nth-child(even) { background-color: #f8f9fa; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>${surveyData?.title || 'Survey Responses'}</h1>
        <div class="meta">Total Responses: ${responses.length} | Generated: ${new Date().toLocaleString()}</div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              ${questions.map(q => `<th>${q.title}</th>`).join('')}
              <th>Added By</th>
              <th>Added At</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); }
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  if (loading && surveys.length === 0) {
    return (
      <div className="container">
        <div className="loading">Loading Forms...</div>
      </div>
    );
  }

  // Show survey list
  if (!selectedSurvey) {
    return (
      <div>
        <div className="container">
          <h1 className="page-title">Form Responses</h1>
          <p className="page-subtitle">Select a form to view responses</p>
          {error && <div className="error">{error}</div>}
        </div>

        {surveys.length === 0 ? (
          <div className="container empty-state">
            <div className="empty-state-icon">📋</div>
            <h2>No forms yet</h2>
            <p>Create a form first to collect responses</p>
          </div>
        ) : (
          surveys.map((survey) => (
            <div key={survey._id} className="container survey-card-clickable">
              <div 
                onClick={() => loadSurveyResponses(survey._id)}
                style={{ cursor: 'pointer' }}
              >
                <h3 className="card-title">{survey.title}</h3>
                {survey.description && (
                  <p className="card-text">{survey.description}</p>
                )}
                <p style={{ fontSize: '0.85rem', color: '#95a5a6', marginTop: '0.5rem' }}>
                  Created: {new Date(survey.createdAt).toLocaleDateString()}
                </p>
                <div style={{ 
                  marginTop: '0.75rem', 
                  color: '#3498db',
                  fontSize: '0.9rem',
                  fontWeight: 600 
                }}>
                  Click to view responses →
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  // Show responses table
  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading responses...</div>
      </div>
    );
  }

  const questions = getQuestions();

  return (
    <div>
      <div className="container">
        <button 
          onClick={handleBack} 
          className="button button-secondary"
          style={{ marginBottom: '1rem' }}
        >
          ← Back to Forms
        </button>
        
        <h1 className="page-title">{surveyData?.title || 'Survey Responses'}</h1>
        {surveyData?.description && (
          <p className="page-subtitle">{surveyData.description}</p>
        )}
        
        {error && <div className="error">{error}</div>}

        {responses.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📊</div>
            <h2>No responses yet</h2>
            <p>No one has completed this form yet</p>
          </div>
        ) : (
          <div style={{ marginTop: '1rem', color: '#7f8c8d', fontSize: '0.9rem' }}>
            Total responses: <strong>{responses.length}</strong>
          </div>
        )}
      </div>

      {responses.length > 0 && (
        <>
          <div className="container" style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button 
            onClick={exportToExcel}
            className="button button-success"
          >
            📊 Export to Excel
          </button>
          <button 
            onClick={exportToPDF}
            className="button button-success"
          >
            📄 Export to PDF
          </button>
          <button 
            onClick={() => setShowCustomColumnModal(true)}
            className="button"
          >
            ➕ Add Custom Column
          </button>
        </div>

      {/* Custom Column Modal */}
      
      {showCustomColumnModal && (
        <div className="modal-overlay" onClick={() => setShowCustomColumnModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '1rem' }}>Add Custom Column</h2>
            
            <div className="form-group">
              <label className="form-label">Column Name *</label>
              <input
                type="text"
                className="form-input"
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                placeholder="e.g., Total Score"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Formula Type *</label>
              <select 
                className="form-input"
                value={formulaType}
                onChange={(e) => setFormulaType(e.target.value)}
              >
                <option value="sum">Sum - Add numbers</option>
                <option value="average">Average - Calculate average</option>
                <option value="concat">Concatenate - Join with space</option>
                <option value="merge">Merge - Join with comma</option>
                <option value="count">Count - Count non-empty values</option>
                <option value="custom">Custom Formula</option>
              </select>
            </div>

            {formulaType === 'custom' ? (
              <div className="form-group">
                <label className="form-label">Custom Formula *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newColumnFormula}
                  onChange={(e) => setNewColumnFormula(e.target.value)}
                  placeholder="e.g., question1 + question2 * 2"
                />
                <small style={{ color: '#7f8c8d', fontSize: '0.85rem', marginTop: '0.5rem', display: 'block' }}>
                  Use column names: {questions.map(q => q.name).join(', ')}
                </small>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Select Columns *</label>
                
                {/* Selected Tags Display */}
                <div style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: '4px', 
                  padding: '0.5rem',
                  minHeight: '45px',
                  marginBottom: '0.5rem',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  alignItems: 'center',
                  background: 'white'
                }}>
                  {newColumnFormula.split(',').filter(Boolean).map((col, idx) => {
                    const question = questions.find(q => q.name === col.trim());
                    return (
                      <span
                        key={idx}
                        style={{
                          background: '#3498db',
                          color: 'white',
                          padding: '0.4rem 0.6rem',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem'
                        }}
                      >
                        {question?.title || col.trim()}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const cols = newColumnFormula.split(',').filter(Boolean);
                            cols.splice(idx, 1);
                            setNewColumnFormula(cols.join(','));
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '1.1rem',
                            padding: 0,
                            lineHeight: 1
                          }}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                  {newColumnFormula.split(',').filter(Boolean).length === 0 && (
                    <span style={{ color: '#999', fontSize: '0.9rem' }}>
                      Search and select columns below...
                    </span>
                  )}
                </div>

                {/* Search Input with Dropdown */}
                <div className="column-search-container" style={{ position: 'relative', marginBottom: '1rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search columns..."
                    value={columnSearch}
                    onChange={(e) => {
                      setColumnSearch(e.target.value);
                      setShowColumnDropdown(true);
                    }}
                    onFocus={() => setShowColumnDropdown(true)}
                    style={{
                      paddingRight: '2.5rem'
                    }}
                  />
                  {columnSearch && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setColumnSearch('');
                      }}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: '#999',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        padding: 0
                      }}
                    >
                      ×
                    </button>
                  )}

                  {/* Dropdown List */}
                  {showColumnDropdown && (
                    <div 
                      className="column-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        background: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        zIndex: 10,
                        marginTop: '2px'
                      }}
                    >
                      {questions
                        .filter(q => 
                          q.title.toLowerCase().includes(columnSearch.toLowerCase()) ||
                          q.name.toLowerCase().includes(columnSearch.toLowerCase())
                        )
                        .map((q, idx) => {
                          const isSelected = newColumnFormula.split(',').map(s => s.trim()).includes(q.name);
                          return (
                            <div
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                const currentCols = newColumnFormula.split(',').map(s => s.trim()).filter(Boolean);
                                if (isSelected) {
                                  const filtered = currentCols.filter(c => c !== q.name);
                                  setNewColumnFormula(filtered.join(','));
                                } else {
                                  setNewColumnFormula([...currentCols, q.name].join(','));
                                }
                              }}
                              style={{
                                padding: '0.75rem',
                                cursor: 'pointer',
                                background: isSelected ? '#e3f2fd' : 'white',
                                borderBottom: idx < questions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                transition: 'background 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = isSelected ? '#bbdefb' : '#f5f5f5'}
                              onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#e3f2fd' : 'white'}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                readOnly
                                style={{ cursor: 'pointer' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 500, color: '#2c3e50' }}>
                                  {q.title}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                                  {q.name}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      {questions.filter(q => 
                        q.title.toLowerCase().includes(columnSearch.toLowerCase()) ||
                        q.name.toLowerCase().includes(columnSearch.toLowerCase())
                      ).length === 0 && (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>
                          No columns found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Close Dropdown Button */}
                {showColumnDropdown && (
                  <button
                    onClick={() => setShowColumnDropdown(false)}
                    className="button button-secondary button-small"
                    style={{ marginBottom: '1rem', width: '100%' }}
                  >
                    Close Selection
                  </button>
                )}
                
                <small style={{ color: '#7f8c8d', fontSize: '0.85rem', display: 'block' }}>
                  {newColumnFormula.split(',').filter(Boolean).length} column(s) selected
                </small>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button onClick={addCustomColumn} className="button button-success">
                Add Column
              </button>
              <button onClick={() => {
                setShowCustomColumnModal(false);
                setNewColumnName('');
                setNewColumnFormula('');
                setFormulaType('sum');
                setColumnSearch('');
                setShowColumnDropdown(false);
              }} className="button button-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Show Custom Columns */}
      {customColumns.length > 0 && (
        <div className="container" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>Custom Columns:</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {customColumns.map(col => (
              <div key={col.id} style={{ 
                background: '#e3f2fd', 
                padding: '0.5rem 1rem', 
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span><strong>{col.name}</strong> ({col.type})</span>
                <button 
                  onClick={() => deleteCustomColumn(col.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#e74c3c',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    padding: 0
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

        <div className="container" style={{ overflowX: 'auto', padding: 0 }}>
          <table className="response-table">
            <thead>
              <tr>
                <th>#</th>
                {allColumnsOrder.map((col, idx) => {
                  if (col.type === 'question') {
                    const q = questions[col.index];
                    return (
                      <th
                        key={`question-${col.index}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDragEnd={handleDragEnd}
                        style={{ cursor: 'move' }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          ⋮⋮ {q.title}
                        </span>
                      </th>
                    );
                  } else if (col.type === 'custom') {
                    const customCol = customColumns.find(c => c.id === col.id);
                    if (!customCol) return null;
                    return (
                      <th 
                        key={`custom-${col.id}`} 
                        style={{ backgroundColor: '#3498db', cursor: 'move' }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDragEnd={handleDragEnd}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          ⋮⋮ {customCol.name} (Custom)
                        </span>
                      </th>
                    );
                  } else if (col.type === 'metadata') {
                    return (
                      <th 
                        key={`metadata-${col.id}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDragEnd={handleDragEnd}
                        style={{ cursor: 'move' }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          ⋮⋮ {col.id === 'addedBy' ? 'Added By' : 'Added At'}
                        </span>
                      </th>
                    );
                  }
                  return null;
                })}
              </tr>
            </thead>
            <tbody>
              {responses.map((response, idx) => {
                const responseData = response.responses || {};
                
                return (
                  <tr key={response._id}>
                    <td>{idx + 1}</td>
                    {allColumnsOrder.map((col) => {
                      if (col.type === 'question') {
                        const q = questions[col.index];
                        return (
                          <td key={`${response._id}-question-${col.index}`}>
                            {formatValue(responseData[q.name])}
                          </td>
                        );
                      } else if (col.type === 'custom') {
                        const customCol = customColumns.find(c => c.id === col.id);
                        if (!customCol) return null;
                        return (
                          <td key={`${response._id}-custom-${col.id}`} style={{ backgroundColor: '#f0f8ff', fontWeight: 'bold' }}>
                            {calculateCustomValue(customCol, responseData)}
                          </td>
                        );
                      } else if (col.type === 'metadata') {
                        if (col.id === 'addedBy') {
                          return (
                            <td key={`${response._id}-addedBy`} className="user-name">
                              {response.userName || 'Unknown'}
                            </td>
                          );
                        } else {
                          return (
                            <td key={`${response._id}-addedAt`} className="date-time">
                              {new Date(response.submittedAt).toLocaleString()}
                            </td>
                          );
                        }
                      }
                      return null;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

export default ResponsesList;